import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Radio,
  Upload,
  Button,
  Typography,
  Alert,
  Spin,
  message,
} from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { EstimateLineItems } from '../Estimates';
import { EstimateLineItem, Part } from '../../types';
import { AuthContext } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getPromoCodes, PromoCode } from '../../services/promoCodesApi';
import {
  BANNERS,
  BannerDesign,
  CustomerPromotion,
  CustomerPromotionRow,
  money,
} from './banners';
import * as promotionsApi from '../../services/api/promotionsApi';
import type {
  FetchedTemplate,
  Promotion,
  PromotionInput,
  SavePromotionTemplateInput,
} from '../../services/api/promotionsApi';
// Imported directly from the util module, NOT via promotionsApi — this
// component's tests `vi.mock('.../promotionsApi')` wholesale, which would
// turn this pure price-math function into a mock too if it were called
// through that namespace.
import { previewPriceFromDiscount, PromotionDiscountType as DiscountType } from '../../utils/promotionPricing';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Human labels for the eight banner designs (Task 14, components/Promotions/banners).
 * Matches each banner's own "Design N: <name>" doc comment.
 */
export const BANNER_LABELS: Record<BannerDesign, string> = {
  'services-list': 'Services we offer',
  'photo-wide': 'Full-width photo',
  'three-up': 'Three across',
  strip: 'Thin strip',
  bundle: 'Bundle',
  coupon: 'Coupon',
  ladder: 'Was / Now',
  'due-next': 'Due next',
};

const BANNER_DESIGN_ORDER = Object.keys(BANNER_LABELS) as BannerDesign[];

interface PromotionFormValues {
  name: string;
  headline?: string;
  body?: string;
  fine_print?: string;
  list_price?: number;
  discount_mode?: DiscountType;
  discount_value?: number;
  starts_at?: Dayjs;
  ends_at?: Dayjs;
  promo_code_id?: number;
}

export interface PromotionBuilderProps {
  /**
   * The promotion's id from the route (`/owner/promotions/:id`), as a string —
   * `undefined` (or the literal `"new"`) means "create a new promotion". Passed
   * as a prop rather than read via `useParams` so this component renders
   * standalone (e.g. under test) without a Router ancestor; the route wrapper
   * in App.tsx supplies it.
   */
  id?: string;
  /** Called after a successful save, with the saved promotion. */
  onSaved?: (promotion: Promotion) => void;
  /** Called when the owner wants to leave the builder (e.g. back to the list). */
  onBack?: () => void;
}

/**
 * A fallback identity for `AuthContext`, used ONLY when no `AuthProvider`
 * ancestor exists (this component reached that way only in isolation, e.g. a
 * unit test rendering `<PromotionBuilder />` directly). In the real app this
 * screen is always reached through `/owner/*`, which `PrivateRoute` already
 * gates to `role: 'owner'` — so a missing provider can only mean "not the
 * real app", and treating it as an owner is the only sensible default for a
 * screen that is owner-only everywhere it actually renders.
 */
type AuthValue = NonNullable<React.ContextType<typeof AuthContext>>;
const FALLBACK_OWNER_AUTH: AuthValue = {
  user: {
    id: 0,
    email: 'owner@local',
    role: 'owner',
    first_name: 'Owner',
    last_name: '',
    status: 'active',
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
  },
  profile: null,
  loading: false,
  isAuthenticated: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  changePassword: async () => {},
  refreshUser: async () => {},
};

/**
 * Converts the builder's `ServiceItemsTable` lines into the payload
 * `/admin/estimate-templates` expects (backend/src/routes/estimate-templates.ts
 * createTemplateSchema). Carries each part's cost/markup through to
 * `template_parts` (migration 215) — the one thing standing between a
 * promotion and a real margin.
 */
function buildTemplatePayload(name: string, lineItems: EstimateLineItem[]): SavePromotionTemplateInput {
  const totalLaborHours = lineItems.reduce((sum, item) => sum + (item.labor_hours || 0), 0);
  return {
    template_name: name || 'Promotion',
    category: 'Promotions',
    is_public: false,
    is_active: true,
    is_general: false,
    // Schema requires a positive number — a promotion with only parts (no
    // labor hours entered) still needs a value here, so it floors at 0.1
    // rather than failing to save.
    estimated_duration_hours: Math.max(0.1, totalLaborHours),
    line_items: lineItems.map((item) => ({
      category: item.category || 'Service',
      description: item.description || 'Service',
      quantity: item.quantity || 1,
      labor_hours: item.labor_hours ?? null,
      hourly_rate: item.hourly_rate ?? null,
      labor_discount_percentage: item.labor_discount_percentage ?? null,
      labor_discount_amount: item.labor_discount_amount ?? null,
      total_price: item.total_price || 0,
      notes: item.notes ?? null,
      parts: (item.parts || []).map((part) => ({
        // part_number is required non-empty by the schema; a hand-added part
        // with no number on file still needs to save.
        part_number: part.part_number?.trim() || 'N/A',
        description: part.description || 'Part',
        quantity: part.quantity || 1,
        unit_price: part.unit_price || 0,
        total_price: part.total_price || 0,
        discount_percentage: part.discount_percentage ?? 0,
        discount_amount: part.discount_amount ?? 0,
        discounted_price: part.discounted_price ?? null,
        availability: part.availability || 'in_stock',
        supplier: part.supplier ?? null,
        notes: part.notes ?? null,
        cost_cents: part.cost != null ? Math.round(part.cost * 100) : null,
        markup_percentage: part.markup_percentage ?? null,
        markup_preset_id: part.markup_preset_id ?? null,
      })),
    })),
  };
}

/** The reverse of buildTemplatePayload — reopens a saved template's lines for editing. */
function mapTemplateToLineItems(template: FetchedTemplate): EstimateLineItem[] {
  return (template.line_items || []).map((line) => ({
    category: line.category,
    description: line.description,
    quantity: line.quantity,
    labor_hours: line.labor_hours ?? undefined,
    hourly_rate: line.hourly_rate ?? undefined,
    labor_discount_percentage: line.labor_discount_percentage ?? undefined,
    labor_discount_amount: line.labor_discount_amount ?? undefined,
    total_price: line.total_price,
    notes: line.notes ?? undefined,
    parts: (line.parts || []).map(
      (part): Part => ({
        part_number: part.part_number,
        description: part.description,
        quantity: part.quantity,
        unit_price: part.unit_price,
        cost: part.cost_cents != null ? part.cost_cents / 100 : null,
        total_price: part.total_price,
        discount_percentage: part.discount_percentage ?? undefined,
        discount_amount: part.discount_amount ?? undefined,
        discounted_price: part.discounted_price ?? undefined,
        availability: part.availability,
        supplier: part.supplier ?? undefined,
        notes: part.notes ?? undefined,
        markup_percentage: part.markup_percentage ?? null,
        markup_preset_id: part.markup_preset_id ?? null,
      }),
    ),
  }));
}

/**
 * The promotion builder — "basically an estimate the owner can add parts to,
 * discount, and see margins on," labeled with a name and a banner look.
 *
 * Three panes: the job (ServiceItemsTable, left), the offer (middle), and
 * margin + live banner preview (right). See PromotionList for the list this
 * screen is reached from.
 */
const PromotionBuilder: React.FC<PromotionBuilderProps> = ({ id, onSaved, onBack }) => {
  const outerAuth = useContext(AuthContext);
  const isMobile = useIsMobile();
  const [form] = Form.useForm<PromotionFormValues>();

  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [bannerDesign, setBannerDesign] = useState<BannerDesign | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);

  const [promotionId, setPromotionId] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  // New promotions start active (matches PromotionModel.create's own default of 0 being
  // overridden here — an owner building one clearly means to turn it on). Editing one
  // that's paused must NOT silently flip it back on: this is loaded from the existing
  // row and carried through save unchanged, same as show_on_receipts/show_on_portal.
  const [isActive, setIsActive] = useState(1);
  const [showOnReceipts, setShowOnReceipts] = useState(1);
  const [showOnPortal, setShowOnPortal] = useState(1);

  const [margin, setMargin] = useState<promotionsApi.PromotionMargin | null>(null);
  const [marginLoading, setMarginLoading] = useState(false);

  const isEditing = promotionId != null;

  // Live-reactive form values for the price preview / banner preview — useWatch,
  // not getFieldValue, so these re-render on every keystroke (see [[parts-markup]]:
  // a render-time getFieldValue read goes stale after setFieldValue).
  const nameValue = Form.useWatch('name', form);
  const headlineValue = Form.useWatch('headline', form);
  const bodyValue = Form.useWatch('body', form);
  const finePrintValue = Form.useWatch('fine_print', form);
  const listPriceValue = Form.useWatch('list_price', form);
  const discountModeValue = (Form.useWatch('discount_mode', form) as DiscountType | undefined) ?? 'percentage';
  const discountValueValue = Form.useWatch('discount_value', form);
  const endsAtValue = Form.useWatch('ends_at', form);
  const promoCodeIdValue = Form.useWatch('promo_code_id', form);

  useEffect(() => {
    let cancelled = false;
    getPromoCodes()
      .then((codes) => { if (!cancelled) setPromoCodes(codes); })
      .catch(() => { if (!cancelled) setPromoCodes([]); });
    return () => { cancelled = true; };
  }, []);

  const refreshMargin = useCallback(async (pid: number) => {
    setMarginLoading(true);
    try {
      const m = await promotionsApi.getPromotionMargin(pid);
      setMargin(m);
    } catch {
      setMargin(null);
    } finally {
      setMarginLoading(false);
    }
  }, []);

  // Load an existing promotion (and its template's lines) when editing.
  useEffect(() => {
    if (!id || id === 'new') return;
    const numericId = Number(id);
    if (!Number.isInteger(numericId)) return;
    let cancelled = false;

    (async () => {
      setLoadingExisting(true);
      try {
        const all = await promotionsApi.listPromotions();
        const existing = all.find((p) => p.id === numericId);
        if (!existing) {
          if (!cancelled) message.error('Promotion not found.');
          return;
        }
        if (cancelled) return;

        setPromotionId(existing.id);
        setTemplateId(existing.template_id);
        setBannerDesign(existing.banner_design);
        setPhotoUrl(existing.image_url ?? null);
        setIsActive(existing.is_active);
        setShowOnReceipts(existing.show_on_receipts);
        setShowOnPortal(existing.show_on_portal);
        form.setFieldsValue({
          name: existing.name,
          headline: existing.headline ?? undefined,
          body: existing.body ?? undefined,
          fine_print: existing.fine_print ?? undefined,
          list_price: existing.list_price_cents != null ? existing.list_price_cents / 100 : undefined,
          discount_mode: existing.discount_type ?? 'percentage',
          discount_value: existing.discount_value ?? undefined,
          promo_code_id: existing.promo_code_id ?? undefined,
          starts_at: existing.starts_at ? dayjs(existing.starts_at) : undefined,
          ends_at: existing.ends_at ? dayjs(existing.ends_at) : undefined,
        });

        if (existing.template_id) {
          try {
            const template = await promotionsApi.getPromotionTemplate(existing.template_id);
            if (!cancelled) setLineItems(mapTemplateToLineItems(template));
          } catch {
            // Template may have been deleted independently — leave lines empty
            // rather than blocking the rest of the promotion from loading.
          }
        }

        await refreshMargin(existing.id);
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Customer price preview — same rounding rules as the server's priceFromDiscount,
  // computed here purely so it updates live without a request per keystroke.
  const pricePreview = useMemo(() => {
    const listCents = Math.round((listPriceValue ?? 0) * 100);
    if (listCents <= 0 || discountValueValue == null || Number.isNaN(discountValueValue)) return null;
    return previewPriceFromDiscount(listCents, {
      type: discountModeValue,
      value: discountValueValue,
    });
  }, [listPriceValue, discountModeValue, discountValueValue]);

  // The banner preview object — exactly the shape a real /insert customer would
  // eventually see (CustomerPromotion), built locally from the in-progress form
  // + line items. Price/rows only — cost and margin never appear here.
  const previewPromotion: CustomerPromotion = useMemo(() => {
    const listCents = listPriceValue != null && listPriceValue > 0 ? Math.round(listPriceValue * 100) : null;
    const promoCents =
      listCents != null && discountValueValue != null
        ? previewPriceFromDiscount(listCents, { type: discountModeValue, value: discountValueValue }).promoCents
        : listCents;
    const selectedCode = promoCodes.find((pc) => pc.id === promoCodeIdValue);
    const rows: CustomerPromotionRow[] = lineItems.map((item) => ({
      label: item.description || 'Service',
      priceCents: Math.round((item.total_price || 0) * 100),
      wasCents: null,
      percentOff: null,
    }));

    return {
      id: promotionId ?? 0,
      name: nameValue || 'Untitled Promotion',
      headline: headlineValue || null,
      body: bodyValue || null,
      finePrint: finePrintValue || null,
      bannerDesign: bannerDesign ?? 'services-list',
      imageUrl: photoUrl,
      endsAt: endsAtValue ? (endsAtValue as Dayjs).format('YYYY-MM-DD') : null,
      totalCents: promoCents,
      wasTotalCents: listCents,
      promoCode: selectedCode?.code ?? null,
      rows,
    };
  }, [
    listPriceValue, discountValueValue, discountModeValue, promoCodes, promoCodeIdValue,
    lineItems, promotionId, nameValue, headlineValue, bodyValue, finePrintValue,
    bannerDesign, photoUrl, endsAtValue,
  ]);

  const handlePhotoUpload: UploadProps['beforeUpload'] = (file) => {
    setUploadingPhoto(true);
    promotionsApi
      .uploadPromotionPhoto(file as unknown as File)
      .then((url) => setPhotoUrl(url))
      .catch(() => message.error('Failed to upload photo.'))
      .finally(() => setUploadingPhoto(false));
    return false; // we upload it ourselves; don't let antd also try
  };

  const handleSave = async () => {
    let values: PromotionFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!bannerDesign) {
      setBannerError('Choose a banner design before saving.');
      return;
    }
    setBannerError(null);
    setSaving(true);
    try {
      let nextTemplateId = templateId;
      if (lineItems.length > 0) {
        const payload = buildTemplatePayload(values.name, lineItems);
        if (nextTemplateId) {
          await promotionsApi.updatePromotionTemplate(nextTemplateId, payload);
        } else {
          const created = await promotionsApi.createPromotionTemplate(payload);
          nextTemplateId = created.id;
          setTemplateId(created.id);
        }
      }

      const discountMode = values.discount_mode ?? 'percentage';
      const promotionPayload: PromotionInput = {
        name: values.name,
        headline: values.headline || null,
        body: values.body || null,
        fine_print: values.fine_print || null,
        banner_design: bannerDesign,
        discount_type: values.discount_value != null ? discountMode : null,
        discount_value: values.discount_value ?? null,
        list_price_cents: values.list_price != null ? Math.round(values.list_price * 100) : null,
        promo_code_id: values.promo_code_id ?? null,
        starts_at: values.starts_at ? values.starts_at.toISOString() : null,
        ends_at: values.ends_at ? values.ends_at.toISOString() : null,
        template_id: nextTemplateId,
        image_url: photoUrl,
        is_active: isActive,
        show_on_receipts: showOnReceipts,
        show_on_portal: showOnPortal,
      };

      const saved = promotionId
        ? await promotionsApi.updatePromotion(promotionId, promotionPayload)
        : await promotionsApi.createPromotion(promotionPayload);

      setPromotionId(saved.id);
      message.success('Promotion saved.');
      await refreshMargin(saved.id);
      onSaved?.(saved);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to save promotion.');
    } finally {
      setSaving(false);
    }
  };

  const PreviewBanner = bannerDesign ? BANNERS[bannerDesign] : null;

  return (
    <AuthContext.Provider value={outerAuth ?? FALLBACK_OWNER_AUTH}>
      <div style={{ padding: isMobile ? 12 : 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {isEditing ? 'Edit Promotion' : 'New Promotion'}
          </Title>
          <Button.Group>
            {onBack && <Button onClick={onBack}>Back</Button>}
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save Promotion
            </Button>
          </Button.Group>
        </Row>

        {bannerError && (
          <Alert type="error" showIcon message={bannerError} style={{ marginBottom: 16 }} closable onClose={() => setBannerError(null)} />
        )}

        {loadingExisting ? (
          <Spin />
        ) : (
          <Row gutter={16}>
            {/* Left: the job — the exact same line editor an estimate uses. */}
            <Col xs={24} lg={8} style={{ marginBottom: 16 }}>
              <Card size="small" title="The Job">
                <EstimateLineItems
                  items={lineItems}
                  isMobile={isMobile}
                  isLocked={false}
                  onItemsChange={setLineItems}
                />
              </Card>
            </Col>

            {/* Middle: the offer. */}
            <Col xs={24} lg={8} style={{ marginBottom: 16 }}>
              <Card size="small" title="The Offer">
                <Form form={form} layout="vertical" initialValues={{ discount_mode: 'percentage' }}>
                  <Form.Item label="Promotion Name" name="name" rules={[{ required: true, message: 'Name the promotion' }]}>
                    <Input placeholder="Spring Brake Special" />
                  </Form.Item>
                  <Form.Item label="Headline" name="headline">
                    <Input placeholder="Shown at the top of the banner" />
                  </Form.Item>
                  <Form.Item label="Description" name="body">
                    <TextArea rows={2} placeholder="What the customer gets" />
                  </Form.Item>
                  <Form.Item label="Fine Print" name="fine_print">
                    <Input placeholder="Restrictions, expiration, etc." />
                  </Form.Item>
                  <Form.Item label="List Price" name="list_price">
                    <InputNumber prefix="$" min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Price By" name="discount_mode">
                    <Select
                      options={[
                        { value: 'percentage', label: 'Percent off' },
                        { value: 'amount', label: 'Amount off' },
                        { value: 'fixed_price', label: 'New total' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item label="Discount" name="discount_value">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>

                  {pricePreview && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                      <Text strong style={{ fontSize: 20, display: 'block' }}>{money(pricePreview.promoCents)}</Text>
                      <Text type="secondary">Customer pays</Text>
                      {pricePreview.savingCents > 0 && (
                        <div>
                          <Text type="success">
                            Save {money(pricePreview.savingCents)} ({pricePreview.percentOff}% off)
                          </Text>
                        </div>
                      )}
                    </div>
                  )}

                  <Form.Item label="Starts" name="starts_at">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Ends" name="ends_at">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Promo Code" name="promo_code_id">
                    <Select
                      allowClear
                      placeholder="No code required"
                      options={promoCodes.map((pc) => ({ value: pc.id, label: pc.code }))}
                    />
                  </Form.Item>
                  <Form.Item label="Photo">
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={handlePhotoUpload}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadingPhoto}>
                        {photoUrl ? 'Replace Photo' : 'Upload Photo'}
                      </Button>
                    </Upload>
                    {photoUrl && (
                      <img src={photoUrl} alt="Promotion" style={{ maxWidth: '100%', marginTop: 8, borderRadius: 4 }} />
                    )}
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            {/* Right: margin (owner-only) + the live banner preview. */}
            <Col xs={24} lg={8} style={{ marginBottom: 16 }}>
              <Card size="small" title="Shop Margins (owner only)" style={{ marginBottom: 16 }}>
                {marginLoading ? (
                  <Spin size="small" />
                ) : margin ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Cost</span><span>{money(margin.costCents)}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex', justifyContent: 'space-between', fontWeight: 600,
                        color: margin.profitCents >= 0 ? '#52c41a' : '#ff4d4f',
                      }}
                    >
                      <span>Profit</span><span>{money(margin.profitCents)} ({margin.percent}%)</span>
                    </div>
                    {!margin.laborCostIncluded && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message="Parts-only margin — labor cost isn't priced on this promotion's lines."
                      />
                    )}
                  </div>
                ) : (
                  <Text type="secondary">Save the promotion to see shop margins.</Text>
                )}
              </Card>

              <Card size="small" title="Banner Design">
                <Radio.Group
                  value={bannerDesign}
                  onChange={(e) => { setBannerDesign(e.target.value); setBannerError(null); }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}
                >
                  {BANNER_DESIGN_ORDER.map((design) => (
                    <Radio key={design} value={design}>{BANNER_LABELS[design]}</Radio>
                  ))}
                </Radio.Group>

                {PreviewBanner ? (
                  <div data-testid="banner-preview">
                    <PreviewBanner promotion={previewPromotion} />
                  </div>
                ) : (
                  <Text type="secondary">Pick a design above to preview the banner here.</Text>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </AuthContext.Provider>
  );
};

export default PromotionBuilder;
